import React from 'react';
import {
    HStack,
    Button,
    Text,
    Select,
    Box,
    Flex,
    useColorModeValue,
    IconButton,
    Tooltip
} from '@chakra-ui/react';
import {
    MdFirstPage,
    MdLastPage,
    MdChevronLeft,
    MdChevronRight
} from 'react-icons/md';

/**
 * Reusable Pagination Controls Component
 */
function PaginationControls({
    currentPage = 1,
    totalPages = 1,
    totalItems = 0,
    itemsPerPage = 20,
    onPageChange,
    onPageSizeChange,
    pageSizeOptions = [10, 20, 50, 100],
    showPageSizeSelector = true,
    showPageInfo = true,
    showFirstLast = true,
    size = 'md',
    isLoading = false
}) {
    // Theme-aware styling
    const textColor = useColorModeValue('gray.600', 'gray.300');
    const borderColor = useColorModeValue('gray.200', 'gray.600');

    /**
     * Handle page change with validation
     */
    const handlePageChange = (newPage) => {
        if (newPage >= 1 && newPage <= totalPages && onPageChange && !isLoading) {
            onPageChange(newPage);
        }
    };

    /**
     * Handle page size change
     */
    const handlePageSizeChange = (event) => {
        const newSize = parseInt(event.target.value);
        if (onPageSizeChange && !isLoading) {
            onPageSizeChange(newSize);
        }
    };

    /**
     * Calculate display range
     */
    const startItem = totalItems === 0 ? 0 : (currentPage - 1) * itemsPerPage + 1;
    const endItem = Math.min(currentPage * itemsPerPage, totalItems);

    /**
     * Generate page numbers for display
     */
    const getPageNumbers = () => {
        const delta = 2; // Number of pages to show on each side
        const pages = [];
        const start = Math.max(1, currentPage - delta);
        const end = Math.min(totalPages, currentPage + delta);

        // Add ellipsis and first page if needed
        if (start > 1) {
            pages.push(1);
            if (start > 2) {
                pages.push('...');
            }
        }

        // Add page numbers
        for (let i = start; i <= end; i++) {
            pages.push(i);
        }

        // Add ellipsis and last page if needed
        if (end < totalPages) {
            if (end < totalPages - 1) {
                pages.push('...');
            }
            pages.push(totalPages);
        }

        return pages;
    };

    // Don't render if no items
    if (totalItems === 0) {
        return null;
    }

    return (
        <Flex
            direction={{ base: 'column', md: 'row' }}
            justify="space-between"
            align={{ base: 'stretch', md: 'center' }}
            gap={4}
            p={4}
            borderTop="1px"
            borderColor={borderColor}
        >
            {/* Page Info */}
            {showPageInfo && (
                <Box>
                    <Text fontSize={size === 'sm' ? 'xs' : 'sm'} color={textColor}>
                        Showing {startItem} to {endItem} of {totalItems} entries
                    </Text>
                </Box>
            )}

            {/* Page Navigation */}
            <HStack spacing={1}>
                {/* First Page */}
                {showFirstLast && (
                    <Tooltip label="First Page" hasArrow>
                        <IconButton
                            icon={<MdFirstPage />}
                            onClick={() => handlePageChange(1)}
                            isDisabled={currentPage === 1 || isLoading}
                            size={size}
                            variant="ghost"
                            aria-label="Go to first page"
                        />
                    </Tooltip>
                )}

                {/* Previous Page */}
                <Tooltip label="Previous Page" hasArrow>
                    <IconButton
                        icon={<MdChevronLeft />}
                        onClick={() => handlePageChange(currentPage - 1)}
                        isDisabled={currentPage === 1 || isLoading}
                        size={size}
                        variant="ghost"
                        aria-label="Go to previous page"
                    />
                </Tooltip>

                {/* Page Numbers */}
                {getPageNumbers().map((page, index) => {
                    if (page === '...') {
                        return (
                            <Text key={`ellipsis-${index}`} px={2} color={textColor}>
                                ...
                            </Text>
                        );
                    }

                    return (
                        <Button
                            key={page}
                            onClick={() => handlePageChange(page)}
                            isDisabled={isLoading}
                            variant={currentPage === page ? 'solid' : 'ghost'}
                            colorScheme={currentPage === page ? 'brand' : 'gray'}
                            size={size}
                            minW="40px"
                        >
                            {page}
                        </Button>
                    );
                })}

                {/* Next Page */}
                <Tooltip label="Next Page" hasArrow>
                    <IconButton
                        icon={<MdChevronRight />}
                        onClick={() => handlePageChange(currentPage + 1)}
                        isDisabled={currentPage === totalPages || isLoading}
                        size={size}
                        variant="ghost"
                        aria-label="Go to next page"
                    />
                </Tooltip>

                {/* Last Page */}
                {showFirstLast && (
                    <Tooltip label="Last Page" hasArrow>
                        <IconButton
                            icon={<MdLastPage />}
                            onClick={() => handlePageChange(totalPages)}
                            isDisabled={currentPage === totalPages || isLoading}
                            size={size}
                            variant="ghost"
                            aria-label="Go to last page"
                        />
                    </Tooltip>
                )}
            </HStack>

            {/* Page Size Selector */}
            {showPageSizeSelector && onPageSizeChange && (
                <HStack spacing={2}>
                    <Text fontSize={size === 'sm' ? 'xs' : 'sm'} color={textColor} whiteSpace="nowrap">
                        Items per page:
                    </Text>
                    <Select
                        value={itemsPerPage}
                        onChange={handlePageSizeChange}
                        size={size}
                        minW="80px"
                        isDisabled={isLoading}
                    >
                        {pageSizeOptions.map(option => (
                            <option key={option} value={option}>
                                {option}
                            </option>
                        ))}
                    </Select>
                </HStack>
            )}
        </Flex>
    );
}

export default PaginationControls;